import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import SocialLinks from './SocialLinks';
import { isRTL as isRTLLang } from '../i18n';
import { HEADER_ROUTES, FOOTER_NAV_ROUTES, FOOTER_COMPANY_ROUTES, FOOTER_LEGAL_ROUTES } from '../navigation/routeRegistry';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const rtl = isRTLLang(i18n.language);

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, rtl]);

  // Derived from the single navigation/route registry (header routes), rendered via i18n labels.
  const navLinks = HEADER_ROUTES.map((r) => ({ href: r.path, label: t(r.navLabelKey) }));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black backdrop-blur-xl border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className={`flex items-center justify-between h-16 md:h-20 ${rtl ? 'flex-row-reverse' : ''}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-heading text-xl md:text-2xl font-bold text-white tracking-tight">
              BLACK MATTE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center gap-8 ${rtl ? 'flex-row-reverse' : ''}`} data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-white ${
                  location.pathname === link.href ? 'text-white' : 'text-white/50'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className={`hidden md:flex items-center gap-4 ${rtl ? 'flex-row-reverse' : ''}`}>
            <LanguageSwitcher variant="default" />
            <a href="tel:0939242320" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm">09 39 24 23 20</span>
            </a>
            <Link to="/reserver">
              <Button
                className="bg-black text-white border border-white/20 hover:bg-white/5 hover:border-white/30 px-6 rounded-xl transition-all"
                data-testid="header-book-btn"
              >
                {t('nav.book')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black border-t border-white/10"
          >
            <nav className="flex flex-col p-6 gap-4" data-testid="mobile-nav">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`text-lg font-medium py-2 transition-colors ${
                    location.pathname === link.href ? 'text-white' : 'text-white/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <LanguageSwitcher variant="default" />
                </div>
                <a href="tel:0939242320" className="flex items-center gap-2 text-white/50 mb-4">
                  <Phone className="w-4 h-4" />
                  <span>09 39 24 23 20</span>
                </a>
                <Link to="/reserver" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-black text-white border border-white/20 py-6 rounded-xl">
                    {t('nav.bookNow')}
                  </Button>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const rtl = isRTLLang(i18n.language);

  // Derived from the single navigation/route registry (footer-nav routes), rendered via i18n labels.
  const navLinks = FOOTER_NAV_ROUTES.map((r) => ({ href: r.path, label: t(r.navLabelKey) }));

  return (
    <footer className="bg-black border-t border-white/10" dir={rtl ? 'rtl' : 'ltr'}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl font-bold text-white mb-4">BLACK MATTE</h3>
            <p className="text-white/50 mb-6 max-w-md leading-relaxed">
              {t('footer.description')}
            </p>
            {/* DPA-4 — contact + official social identities (phone kept; Facebook/Instagram/LinkedIn/
                Google Business + WhatsApp added). Uses the existing footer icon-button design language. */}
            <div className="flex flex-wrap gap-3" data-testid="footer-social">
              <a
                href="tel:0939242320"
                className="w-10 h-10 bg-black border border-white/10 rounded-xl flex items-center justify-center text-white/50 hover:text-white hover:border-white/20 transition-all"
                aria-label="Téléphone 09 39 24 23 20"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
              </a>
              <SocialLinks tone="dark" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-3">
              {navLinks.map(link => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-white/50 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company — registry-driven (footer-company routes). Future Careers/Partners/Sustainability
              slot in here with no footer change. Rendered only when at least one company route exists. */}
          {FOOTER_COMPANY_ROUTES.length > 0 && (
            <div>
              <h4 className="font-heading font-semibold text-white mb-4">{t('footer.company')}</h4>
              <ul className="space-y-3">
                {FOOTER_COMPANY_ROUTES.map((r) => (
                  <li key={r.path}>
                    <Link to={r.path} className="text-white/50 hover:text-white transition-colors">
                      {t(r.navLabelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">{t('footer.info')}</h4>
            <ul className="space-y-3">
              {/* Derived from the single navigation/route registry (footer-legal routes). */}
              {FOOTER_LEGAL_ROUTES.map((r) => (
                <li key={r.path}>
                  <Link to={r.path} className="text-white/50 hover:text-white transition-colors">
                    {t(r.navLabelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} Black Matte. {t('footer.copyright')}
          </p>
          <p className="text-white/30 text-sm">
            {t('footer.location')}
          </p>
        </div>
      </div>
    </footer>
  );
};
